import React, { useState, useEffect } from "react";
import { Button, Form, Input as AntInput, notification, Typography, Row, Col } from "antd";
import { useMutation } from "react-query";
import { register } from "../../../service/authService";
import styled from "styled-components";
import { AuthLayout } from "../auth-layout/AuthLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

async function sha256(value: string): Promise<string> {
  const encoded = new TextEncoder().encode(value);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const { Title, Text, Link } = Typography;

const StyledTitle = styled(Title)`
  &.ant-typography {
    margin-bottom: 12px !important;
    color: #FEFFFC !important;
    font-weight: 500;
    text-align: center;
    font-size: clamp(2.5rem, 5vw, 4rem) !important;
  }
`;

const StyledSubtitle = styled(Text)`
  display: block;
  margin-bottom: 48px;
  color: #FEFFFC;
  font-size: 18px;
  text-align: center;
  opacity: 0.9;
`;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 24px;

  .ant-form-item-label > label {
    color: #FEFFFC !important;
    font-size: 14px;
    font-weight: 500;
    height: auto;
  }

  /* Target both regular inputs and password wrappers */
  .ant-input-affix-wrapper,
  .ant-input:not(.ant-input-affix-wrapper > input) {
    height: 52px;
    background-color: #263e44 !important;
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    border-radius: 8px !important;
    color: #FEFFFC !important;
    padding: 0 12px !important;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;

    &:hover, &:focus, &-focused {
      border-color: #EBFED1 !important;
      background-color: #2c474d !important;
      box-shadow: none !important;
    }

    /* Inner input for affix-wrapper (password/prefix) */
    input {
      background-color: transparent !important;
      color: #FEFFFC !important;
      border: none !important;
      height: 100% !important;
      padding: 0 !important;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }

  .ant-input-password-icon {
    color: rgba(255, 255, 255, 0.5) !important;
    &:hover {
      color: #FEFFFC !important;
    }
  }

  .ant-form-item-explain-error {
    font-size: 12px;
    margin-top: 4px;
    color: #ff4d4f;
  }
`;

const StyledButton = styled(Button)``;

const LoginPrompt = styled.div`
  text-align: center;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const StyledLink = styled(Link)`
  color: #FEFFFC !important;
  font-weight: 600;
  text-decoration: underline !important;
  
  &:hover {
    color: #EBFED1 !important;
  }
`;

const PasswordStrengthIndicator = styled.div<{ strength: number }>`
  height: 4px;
  width: 100%;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  margin-top: -12px;
  margin-bottom: 8px;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${(props) => props.strength}%;
    background: ${(props) =>
    props.strength < 33
      ? "#ff4d4f"
      : props.strength < 66
        ? "#faad14"
        : "#EBFED1"};
    transition: all 0.3s ease;
  }
`;

const PasswordStrengthText = styled.div<{ strength: number }>`
  font-size: 12px;
  margin-bottom: 16px;
  color: ${(props) =>
    props.strength < 33
      ? "#ff4d4f"
      : props.strength < 66
        ? "#faad14"
        : "#EBFED1"};
`;

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (password.length >= 12) strength += 25;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 15;
  if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
  return Math.min(strength, 100);
};

export const RegisterFormNew: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read claim token from URL ?claim=TOKEN or localStorage (set by flex_menu_app)
  const claimToken =
    searchParams.get('claim') ||
    localStorage.getItem('pendingClaimToken') ||
    undefined;

  useEffect(() => {
    // Persist claim token to localStorage so it survives page navigation
    const urlToken = searchParams.get('claim');
    if (urlToken) {
      localStorage.setItem('pendingClaimToken', urlToken);
    }
  }, [searchParams]);

  const { mutateAsync, isLoading } = useMutation(register, {
    onSuccess: async (_data, variables) => {
      // Clear the pending claim token after successful registration
      localStorage.removeItem('pendingClaimToken');

      const normalizedEmail = variables.email.trim().toLowerCase();
      const hashedEmail = await sha256(normalizedEmail);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "form_registration_complete",
        user_data: {
          em: hashedEmail,
        },
      });
      navigate("/success_register");
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || t("registerFormNew.errorDuringRegistration");
      const errorDetails = error?.response?.data?.details;

      notification.error({
        message: t("registerFormNew.registrationErrorTitle"),
        description: (
          <div style={{ color: '#1E363C' }}>
            <div>{errorMessage}</div>
            {errorDetails && (
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                {errorDetails.map((detail: string, idx: number) => (
                  <li key={idx}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        ),
        placement: "bottomRight",
        duration: 5,
      });
    },
  });

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setPasswordStrength(calculatePasswordStrength(password));
  };

  const handleSubmit = async (values: any) => {
    const payload: any = { ...values };
    if (claimToken) payload.claimToken = claimToken;
    await mutateAsync(payload);
  };

  return (
    <AuthLayout maxWidth={540}>
      <StyledTitle level={1}>
        {t("registerFormNew.welcomeTitle")}
      </StyledTitle>
      <StyledSubtitle>
        {claimToken
          ? t("registerFormNew.subtitleClaim")
          : t("registerFormNew.subtitleDefault")}
      </StyledSubtitle>

      <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark={false}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <StyledFormItem
              name="firstName"
              label={t("registerFormNew.firstNameLabel")}
              rules={[
                { required: true, message: t("registerFormNew.firstNameRequired") },
                { min: 2, message: t("registerFormNew.firstNameMin") },
                { max: 50, message: t("registerFormNew.firstNameMax") },
                { whitespace: true, message: t("registerFormNew.firstNameWhitespace") },
              ]}
              normalize={(value) => value?.trim()}
            >
              <AntInput
                placeholder={t("registerFormNew.firstNamePlaceholder")}
                size="large"
              />
            </StyledFormItem>
          </Col>
          <Col xs={24} sm={12}>
            <StyledFormItem
              name="lastName"
              label={t("registerFormNew.lastNameLabel")}
              rules={[
                { required: true, message: t("registerFormNew.lastNameRequired") },
                { min: 2, message: t("registerFormNew.lastNameMin") },
                { max: 50, message: t("registerFormNew.lastNameMax") },
                { whitespace: true, message: t("registerFormNew.lastNameWhitespace") },
              ]}
              normalize={(value) => value?.trim()}
            >
              <AntInput
                placeholder={t("registerFormNew.lastNamePlaceholder")}
                size="large"
              />
            </StyledFormItem>
          </Col>
        </Row>

        <StyledFormItem
          name="email"
          label={t("registerFormNew.emailLabel")}
          rules={[
            { required: true, message: t("registerFormNew.emailRequired") },
            {
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: t("registerFormNew.emailInvalid")
            },
          ]}
          normalize={(value) => value?.trim().toLowerCase()}
        >
          <AntInput
            placeholder={t("registerFormNew.emailPlaceholder")}
            size="large"
            autoComplete="email"
          />
        </StyledFormItem>

        <StyledFormItem
          name="password"
          label={t("registerFormNew.passwordLabel")}
          rules={[
            { required: true, message: t("registerFormNew.passwordRequired") },
            { min: 8, message: t("registerFormNew.passwordMin") },
            { max: 128, message: t("registerFormNew.passwordMax") },
          ]}
        >
          <AntInput.Password
            placeholder={t("registerFormNew.passwordPlaceholder")}
            size="large"
            onChange={handlePasswordChange}
            autoComplete="new-password"
          />
        </StyledFormItem>

        {passwordStrength > 0 && (
          <>
            <PasswordStrengthIndicator strength={passwordStrength} />
            <PasswordStrengthText strength={passwordStrength}>
              {t("registerFormNew.passwordStrengthLabel")}{" "}
              {passwordStrength < 33
                ? t("registerFormNew.passwordStrengthWeak")
                : passwordStrength < 66
                  ? t("registerFormNew.passwordStrengthMedium")
                  : t("registerFormNew.passwordStrengthStrong")}
            </PasswordStrengthText>
          </>
        )}

        <StyledFormItem
          name="confirmPassword"
          label={t("registerFormNew.confirmPasswordLabel")}
          dependencies={["password"]}
          rules={[
            { required: true, message: t("registerFormNew.confirmPasswordRequired") },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error(t("registerFormNew.passwordsNotMatch")));
              },
            }),
          ]}
        >
          <AntInput.Password
            placeholder={t("registerFormNew.confirmPasswordPlaceholder")}
            size="large"
            autoComplete="new-password"
          />
        </StyledFormItem>

        <Form.Item style={{ marginBottom: 0 }}>
          <StyledButton type="primary" htmlType="submit" loading={isLoading} block>
            {isLoading ? t("registerFormNew.submitLoading") : t("registerFormNew.submitButton")}
          </StyledButton>
        </Form.Item>
      </Form>

      <LoginPrompt>
        <Text style={{ color: '#FEFFFC', opacity: 0.8 }}>
          {t("registerFormNew.alreadyHaveAccount")}{" "}
          <StyledLink onClick={() => navigate("/login")}>
            {t("registerFormNew.loginLink")}
          </StyledLink>
        </Text>
      </LoginPrompt>
    </AuthLayout>
  );
};
